import { Box } from "@chakra-ui/react";
import React from "react";
import * as AF from "src/types/formTemplate";
import * as RD from "src/types/remoteData";
import { useEditFormTemplateContext } from "../../context";
import { getQuestionMode } from "../../types/state";
import { QuestionFormEdit } from "./Edit";
import { SchoolItem } from "./types";
import { QuestionFormView } from "./View";

type Props = {
  sectionType: AF.V2.SectionType;
  question: AF.V2.Question;
  schools: RD.RemoteData<unknown, SchoolItem[]>;
  onUpdate: (
    question: AF.V2.Question,
    verification: AF.V2.FormVerification | undefined,
    deletedVerificationIds: string[] | undefined
  ) => Promise<void>;
  onRemove: (question: AF.V2.Question) => Promise<void>;
  onArchive: (question: AF.V2.Question) => Promise<void>;
  onUnarchive: (question: AF.V2.Question) => Promise<void>;
  otherQuestions: readonly AF.V2.Question[];
};
export const QuestionForm: React.FC<Props> = ({
  sectionType,
  question,
  onUpdate,
  onRemove,
  onArchive,
  onUnarchive,
  schools,
  otherQuestions,
}) => {
  const { state, dispatch } = useEditFormTemplateContext();
  const mode = getQuestionMode(state, question.id);

  const setMode = React.useCallback(
    (mode: "Edit" | "View") => {
      dispatch({
        type: "SetQuestionMode",
        questionId: question.id,
        mode,
      });
    },
    [dispatch, question.id]
  );

  const onEditButtonClick = React.useCallback(() => {
    setMode("Edit");
  }, [setMode]);

  const onCancelButtonClick = React.useCallback(() => {
    setMode("View");
  }, [setMode]);

  const onUpdateButtonClick = React.useCallback(
    async (
      updatedQuestion: AF.V2.Question,
      verification: AF.V2.FormVerification | undefined,
      deletedVerificationIds: string[] | undefined
    ) => {
      if (onUpdate) {
        await onUpdate(updatedQuestion, verification, deletedVerificationIds);
      }
      setMode("View");
    },
    [onUpdate, setMode]
  );

  const onRemoveButtonClick = React.useCallback(() => {
    onRemove(question);
  }, [onRemove, question]);

  const onArchiveButtonClick = React.useCallback(() => {
    onArchive(question);
  }, [onArchive, question]);

  const onUnarchiveButtonClick = React.useCallback(() => {
    onUnarchive(question);
  }, [onUnarchive, question]);

  let content;
  switch (mode) {
    case "Edit": {
      content = (
        <QuestionFormEdit
          sectionType={sectionType}
          question={question}
          onCancelButtonClick={onCancelButtonClick}
          onUpdate={onUpdateButtonClick}
          schools={schools}
          otherQuestions={otherQuestions}
        />
      );
      break;
    }

    case "View": {
      content = (
        <QuestionFormView
          question={question}
          onEditButtonClick={onEditButtonClick}
          onRemoveButtonClick={onRemoveButtonClick}
          onArchiveButtonClick={onArchiveButtonClick}
          onUnarchiveButtonClick={onUnarchiveButtonClick}
          schools={schools}
        />
      );
      break;
    }

    default:
      const _exhaustiveCheck: never = mode;
      return _exhaustiveCheck;
  }

  return <Box id={question.id}>{content}</Box>;
};

export { QuestionFormEdit } from "./Edit";
export { QuestionFormView } from "./View";
